/*
 *
 * QuestionnairePage reducer
 *
 */

import { LOCATION_CHANGE } from 'connected-react-router';
import {
  getAnswerCondition,
  getTaxonomyCondition,
  updateState,
  initQlAfterFetch,
  updateQlAfterMoveQuestion,
  updateQlAfterQuestionnaireUpdate,
  getQlSubNodes,
  getConceptCondition,
  updateQlQuestionnaire,
  getLogicKeyFromMode,
  getVisibilityCondition,
  findStatementIdByOutcomeActionValue,
  removeCarriedForwardAnswersFromQlByNodeId,
  generateQuotaRuleForAnswer,
  generateQuotaRuleForAnswerColumn,
} from 'containers/QuestionnaireLogic/utils';
import { get, findIndex, noop, cloneDeep, forEach, isFinite } from 'lodash';
import Raven from 'raven-js';
import arrayMove from 'array-move/index';
import { didLocationChangeFromTo } from 'utils/locationChangeMatcher';
import { surveyStatusEnum } from 'containers/SurveysPage/constants';
import {
  UPDATE_SURVEY_SUCCESS,
  UPDATE_SURVEY_COMPLETES,
} from 'containers/Survey/constants';
import { SHUFFLE_NONE } from 'components/DropdownShuffle/constants';
import {
  ADD_STATEMENT,
  DELETE_STATEMENT,
  MOVE_STATEMENT,
  UPDATE_OUTCOME,
  ADD_RULE,
  UPDATE_RULE_OPERATOR,
  UPDATE_RULE_CONTEXT,
  UPDATE_RULE_QUOTA,
  ADD_CONDITION,
  DELETE_CONDITION,
  UPDATE_CONDITION,
  SET_SCROLL_TO_STATEMENT_ID,
  ADD_UNCONDITIONAL_HIDE_ANSWER,
  REMOVE_UNCONDITIONAL_HIDE_ANSWER,
} from 'containers/QuestionnaireLogic/constants';

import {
  UPDATE_PEEQ_PROJECT_COMPLETES,
  REMOVE_TARGETING_CRITERIA,
  ADD_NESTING_TARGETING_CRITERIA,
  REMOVE_NESTING_TARGETING_CRITERIA,
  UPDATE_TARGETING as UPDATE_PEEQ_TARGETING,
  UPDATE_PEEQ_COUNTRY_SUCCESS,
  UPDATE_PEEQ_COUNTRY_MULTI_SUCCESS,
  REMOVE_AUDIENCE_SUCCESS,
} from 'containers/TargetingPeeqPage/constants';
import {
  UPDATE_LINK_SETTINGS,
  FETCH_LINK_SETTINGS_SUCCESS,
  SAVE_LINK_SETTINGS_SUCCESS,
} from 'containers/LinkSettings/constants';

import {
  qlUnifier,
  NodeStatements,
  QuestionnaireConditionContext,
  questionnaireLogicService,
  QuotaConditionTypeEnum,
  QuotaRuleFilter,
} from 'utils/adl-services';

import { getVariableCondition } from 'modules/HiddenVariables/adl-utils';

import { CONDITION_CONTEXTS_SUPPORT_QUOTA } from 'components/LogicQuota/constants';

import { QUESTIONNAIRE_REVISION_REASONS } from 'components/QuestionnaireRevisions/constants';
import { transformUpsertToFetch } from 'containers/AISurveyBuilder/helpers';
import {
  ADD_HIDDEN_VARIABLE,
  UPDATE_HIDDEN_VARIABLE,
  DELETE_HIDDEN_VARIABLE,
  ADD_HIDDEN_VARIABLE_VALUE,
  UPDATE_HIDDEN_VARIABLE_VALUE,
  DELETE_HIDDEN_VARIABLE_VALUE,
} from 'modules/HiddenVariables/constants';

import findQuestionByRefId from 'utils/findQuestionByRefId';
import { addQuotaRulesForSpecificAnswersColumns } from 'utils/quotaHelpers';
import { QUOTA_LIMIT_TYPE_MIN } from 'data/quotaLimitTypes';
import { QUOTA_MODE } from 'data/quotaModes';
import { isBasicGroup, isLoopAndMergeGroup } from 'utils/nodeTypes';
import {
  GROUP_TYPES_ALLOW_ADVANCED_QUOTAS,
  GROUP_TYPES_ALLOW_SIMPLE_QUOTAS,
} from 'data/nodeTypes';
import { isSingleAssignmentStrategy } from 'utils/quotaAssignmentStrategy';
import {
  FETCH_QUESTIONNAIRE,
  FETCH_QUESTIONNAIRE_ERROR,
  FETCH_QUESTIONNAIRE_SUCCESS,
  UPDATE_QUESTIONNAIRE,
  UPDATE_QUESTIONNAIRE_LOGIC,
  ADD_QUESTION_TO_QUESTIONNAIRE,
  STORE_QUESTIONNAIRE,
  STORE_QUESTIONNAIRE_SUCCESS,
  STORE_QUESTIONNAIRE_TEXTS,
  STORE_QUESTIONNAIRE_TEXTS_SUCCESS,
  STORE_DISQUALIFICATION_RULES,
  STORE_DISQUALIFICATION_RULES_SUCCESS,
  REMOVE_QUESTION_FROM_QUESTIONNAIRE,
  MOVE_QUESTION,
  SET_ACTIVE_NODE,
  SHOW_ALL_ERRORS,
  SET_QUESTIONNAIRE_SHUFFLING,
  UPDATE_GROUP_PROPERTIES,
  SHOW_MOVE_QUESTION_MODAL,
  CLOSE_MOVE_QUESTION_MODAL,
  INSERT_RECALL,
  locationResetExceptionRoutes,
  MOVE_NODE,
  MOVE_NODES_TO_GROUP,
  REMOVE_GROUP_KEEP_NODES,
  TOGGLE_MODAL,
  MODALS,
  AB_ADD_CONCEPT,
  AB_UPDATE_CONCEPT,
  AB_REMOVE_CONCEPT,
  GROUP_ADD_ATTRIBUTE,
  GROUP_UPDATE_ATTRIBUTE,
  GROUP_REMOVE_ATTRIBUTE,
  GROUP_REMOVE_LOOP_AND_MERGE_CONCEPT,
  GROUP_UPDATE_ATTRIBUTE_VALUE,
  AB_ADD_DEFAULT_NODES,
  AB_UPDATE_PROPERTIES,
  LOOP_AND_MERGE_UPDATE_PROPERTIES,
  VAN_WESTENDORP_UPDATE_SETTINGS,
  VAN_WESTENDORP_UPDATE_LIKELIHOOD,
  MATRIX_OPEN_ENDED_UPDATE_SETTINGS,
  DISQUALIFY_PLACEHOLDER_QUESTION,
  FETCH_DEMOGRAPHICS,
  FETCH_DEMOGRAPHICS_SUCCESS,
  FETCH_DEMOGRAPHICS_ERROR,
  LOGIC_MODES,
  CONJOINT_UPDATE_PROPERTIES,
  CONJOINT_MOVE_ATTRIBUTE,
  FETCH_DESIGN_OF_EXPERIMENT,
  FETCH_DESIGN_OF_EXPERIMENT_SUCCESS,
  FETCH_DESIGN_OF_EXPERIMENT_ERROR,
  DOE_UPDATE_GROUP_AND_PROPERTIES,
  ANSWER_NONE_OF_THE_ABOVE,
  DUPLICATE_MULTIPLE_QUESTIONS_TO_QUESTIONNAIRE,
  UPDATE_QUESTION_FAVORITE_STATE,
  TOGGLE_QUESTION_COLLAPSE,
  LOGIC_MODE_KEYS,
  SET_REVISION_PREVIEW_MODE,
  SET_REVISION_PREVIEW_MODE_SUCCESS,
  FETCH_QUESTIONNAIRE_REVISIONS,
  FETCH_QUESTIONNAIRE_REVISIONS_SUCCESS,
  FETCH_QUESTIONNAIRE_REVISIONS_ERROR,
  FETCH_QUESTIONNAIRE_BY_REVISION,
  FETCH_QUESTIONNAIRE_BY_REVISION_ERROR,
  SAVE_QUESTIONNAIRE_REVISION,
  SAVE_QUESTIONNAIRE_REVISION_SUCCESS,
  SAVE_QUESTIONNAIRE_REVISION_ERROR,
  LOAD_QUESTIONNAIRE_REVISION,
  DELETE_QUESTIONNAIRE_REVISION,
  DELETE_QUESTIONNAIRE_REVISION_SUCCESS,
  DELETE_QUESTIONNAIRE_REVISION_ERROR,
  RESTORE_QUESTIONNAIRE_REVISION,
  UPDATE_QUESTIONNAIRE_EXTERNAL,
  REVERT_QUESTIONNAIRE_REVISION_LATEST_AI,
  REVERT_QUESTIONNAIRE_REVISION_LATEST_AI_SUCCESS,
  REVERT_QUESTIONNAIRE_REVISION_LATEST_AI_ERROR,
  CLEAR_EXTERNAL_UPSERT_ADDITIONAL_INFO,
  ADD_QUESTION_DIRECTION,
  GROUP_ADD_CONCEPT,
  LOOP_AND_MERGE_UPDATE_GROUP_SUCCESS,
  FETCH_QUESTIONNAIRE_DIFF_SUCCESS,
  GROUP_RE_ORDER_LOOP_AND_MERGE_CONCEPT,
  TOGGLE_NODE_VISIBILITY,
  VALIDATE_ALL_NODES,
  SET_NODE_QUOTA_MODE,
  TOGGLE_NODE_QUOTAS_SUCCESS,
  TOGGLE_NODE_QUOTAS_ERROR,
  CREATE_NODE_QUOTA_RULES,
  REMOVE_NODE_QUOTA_RULES,
  UPDATE_NODE_QUOTA_RULE_VALUE,
  SET_ANSWER_SCREENER_TYPE,
  RESET_SCREENER_TYPES,
} from './constants';

import {
  getNodeErrors,
  getRevalidatedQuestionsAndGlobalErrors,
  updateQuestionnaireErrors,
  getNodeByRefId,
  getNodesOnAdd,
  getNodesOnUpdate,
  getNodesOnRemove,
  getNodesOnMove,
  mapQuestionsAndGroupsAsNodes,
  getRevalidatedNodesAndGlobalErrors,
  clearMentionsFromNodes,
  clearCarryForwardOnNode,
  attachErrorsOnNodes,
  moveNodeOnList,
  hasDemographicTaxonomyWithLogic,
  mergePreviousNodesPerNode,
  getSurveyConfigForValidate,
  deriveSurveyCompletes,
  attachAIGlobalErrors,
  removeAllVariableMentionsFromNode,
  validateQuotaAndUpdateState,
  isGroupAllowedToHaveQuestionWithQuotas,
  removeGroupStatementsReferencingSubnodes,
  getQuotaEligibleOptions,
  getQuotaEligibilityForNode,
  getUnconditionallyHiddenItems,
} from './utils';

import {
  shouldEnforceScreeningBoundary,
  wouldCrossScreeningBoundary,
  wouldCrossScreeningBoundaryOnMove,
  hasScreeningNodeInMoveBatch,
} from './screeningBoundaryUtils';

import {
  addStatementActionHandler,
  updateStatementActionHandler,
  addConditionActionHandler,
  addRuleActionHandler,
  updateRuleOperatorActionHandler,
  updateRuleQuotaActionHandler,
  addUnconditionalHideAnswerActionHandler,
  removeUnconditionalHideAnswerActionHandler,
  toggleVisibilityActionHandler,
  validateAllNodesActionHandler,
} from './reducer_ADL_utils';

import {
  setQuestionnaireShuffling,
  addScreeningQuestionToQuestionnaireActionHandler,
  addMultipleScreeningQuestionsToQuestionnaire,
  removeScreeningQuestionFromQuestionnaireActionHandler,
  removeAllScreeningQuestionFromQuestionnaireActionHandler,
  updateScreeningQuestionInQuestionnaireActionHandler,
  abAddConceptActionHandler,
  vanWestendorpToggleLikelihoodNodesActionHandler,
  vanWestendorpUpdateSettingsHandler,
  matrixOpenEndedUpdateSettingsHandler,
  abUpdateConceptActionHandler,
  abUpdatePropertiesActionHandler,
  abRemoveConceptActionHandler,
  abAddDefaultNodesActionHandler,
  groupUpdateAttributeActionHandler,
  groupAddAttributeActionHandler,
  groupUpdateAttributeValueActionHandler,
  groupRemoveAttributeActionHandler,
  removeQuestionFromQuestionnaireActionHandler,
  saveCheckpointHandler,
  setDefaultRevisionInState,
  restoreDefaultRevisionInState,
  incrementQuestionnaireEditVersion,
  deleteHiddenVariableFromLogic,
  deleteHiddenVariableValueFromLogic,
  cleanLogicFromTaxonomyProperties,
  addQuestionRefIdCorrection,
  updateGroupPropertiesHandler,
  attachHasPipingOnRevisionInState,
  groupAddConceptActionHandler,
  groupRemoveConceptLoopAndMergeActionHandler,
  groupLoopAndMergeUpdateGroupActionHandler,
  groupReOrderLoopAndMergeConcept,
  loopAndMergeUpdatePropertiesActionHandler,
  cleanLoopAndMergeGroupsFromProfilingQuestions,
} from './reducer_utils';
import {
  ADD_SCREENING_QUESTION,
  REMOVE_SCREENING_QUESTION,
  REMOVE_ALL_SCREENING_QUESTIONS,
  UPDATE_SCREENING_QUESTION,
  UPDATE_TARGETING,
  DUPLICATE_MULTIPLE_SCREENING_QUESTIONS,
} from '../TargetingPage/constants';
import { duplicateMultipleQuestionsToQuestionnaire } from './reducerHandlers/duplicateMultipleQuestionsToQuestionnaire';
import addNodeToList from './reducerUtils/addNodeToList';
import {
  resetScreenerTypesHandler,
  setAnswerScreenerTypeHandler,
} from './reducerHandlers/screenerTypes';
import {
  setActiveNode,
  getActiveNodeOrFallback,
} from './reducerHandlers/activeNode';
import updateQuestionFavoriteHandler from './reducerUtils/updateQuestionFavoriteHandler';
import updateConjointOrMaxDiffProperties from './QuestionGroup/reducer_utils/updateConjointOrMaxDiffProperties';
import addNodeMetadata from './reducerUtils/addNodeMetadata';
import { getSurveyLocationResetExceptionRulesForVersionHistory } from '../SurveyPage/constants';

export const initialState = {
  surveyDetailsExtended: {
    questionnaireEditVersion: -1,
  },
  isMoveQuestionModalOpen: false,
  shouldAddEmptyQuestion: false,
  errors: [],
  comments: {},
  activeQuestionId: null,
  activeGroupId: null,
  isFetching: false,
  hasFetched: false,
  isSaving: false,
  isFetchingDoeSettingsData: false,
  doeErrors: [],
  externalUpdateTimestamp: null,
  confirmModal: {
    open: false,
    title: '',
    body: '',
    confirmText: '',
    cancelText: '',
    onConfirm: noop,
    onCancel: noop,
  },
  insertRecallRequest: {},
  activeGroup: null,
  questionGroupShuffle: 0,
  questionnairePage: {}, // TODO: ?do we use this?
  questionnaireLogic: {
    ql: {},
    version: -1,
    error: null,
  },
  disqualificationLogic: {
    ql: {},
    version: -1,
    error: null,
  },
  modals: {
    [MODALS.MOVE_NODES]: {
      visible: false,
    },
    [MODALS.REMOVE_GROUP]: {
      visible: false,
    },
    [MODALS.CONCEPTS_ADD_ASSET]: {
      visible: false,
    },
    [MODALS.VW_ADD_ASSET]: {
      visible: false,
    },
  },
  screeningLogicEnabled: false,
  survey: {
    isUnderEdit: false,
    isDraft: false,
    completes: undefined,
    completed: undefined,
    remainingCompletes: undefined,
  },
  revisions: {
    isInRevisionPreviewMode: false,
    revisionInPreview: null,
    data: {},
    isClosing: false,
    isLoading: false,
    isLoadingRevision: false,
    error: null,
    saveCheckpoint: false,
    saveReason: QUESTIONNAIRE_REVISION_REASONS.AUTO_SAVE,
    questionnaireRevisions: {
      current: null,
    },
  },
  externalUpsertAdditionalInfo: {},
  questionsDiff: {},
  questionnaireQuotaCounters: {
    pendingQuotaMode: {},
    data: [],
  },
};

function questionnairePageNewReducer(state = initialState, action) {
  switch (action.type) {
    case SET_REVISION_PREVIEW_MODE: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isClosing: action.isClosing,
        },
      });
    }
    case SET_REVISION_PREVIEW_MODE_SUCCESS: {
      return Object.assign({}, state, {
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          isQuestionnairePreview: action.isInRevisionPreviewMode,
        },
        revisions: {
          ...state.revisions,
          isLoading: Boolean(action.isInRevisionPreviewMode),
          isClosing: false,
          isInRevisionPreviewMode: action.isInRevisionPreviewMode,
          revisionInPreview: action.revision,
        },
      });
    }
    case FETCH_QUESTIONNAIRE_REVISIONS: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoading: true,
          error: null,
        },
      });
    }
    case FETCH_QUESTIONNAIRE_REVISIONS_SUCCESS: {
      const { autoSaves, userSaves, aiSaves, ...extraProps } = action.payload;
      let newState = Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          data: { autoSaves, userSaves, aiSaves },
          isLoading: false,
          error: null,
        },
        ...extraProps,
      });

      newState = setDefaultRevisionInState(newState);

      return newState;
    }
    case SAVE_QUESTIONNAIRE_REVISION_ERROR:
    case FETCH_QUESTIONNAIRE_REVISIONS_ERROR: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isInRevisionPreviewMode: false,
          isLoading: false,
          error: action.error,
        },
      });
    }
    case FETCH_QUESTIONNAIRE_BY_REVISION: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoadingRevision: true,
        },
      });
    }
    case FETCH_QUESTIONNAIRE_BY_REVISION_ERROR: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoading: false,
          isLoadingRevision: false,
          error: action.error,
        },
      });
    }
    case SAVE_QUESTIONNAIRE_REVISION: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoadingRevision: true,
          error: null,
        },
      });
    }
    case SAVE_QUESTIONNAIRE_REVISION_SUCCESS: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoadingRevision: false,
          error: null,
        },
      });
    }
    case RESTORE_QUESTIONNAIRE_REVISION: {
      const newState = setDefaultRevisionInState(state);

      return Object.assign({}, newState, {
        revisions: {
          ...newState.revisions,
          isLoading: false,
          isLoadingRevision: false,
          error: null,
        },
      });
    }
    case LOAD_QUESTIONNAIRE_REVISION: {
      let newState = { ...state, isFetching: false };
      const { isQuestionnairePreview, checkpointId } = action.payload;

      newState = Object.assign({}, newState, {
        revisions: {
          ...newState.revisions,
          isLoading: false,
          isLoadingRevision: false,
          revisionInPreview: isQuestionnairePreview
            ? checkpointId || null
            : null,
        },
      });

      let stateAfterQuestionnaireUpdate;

      if (isQuestionnairePreview && checkpointId) {
        stateAfterQuestionnaireUpdate = fetchQuestionnaireSuccessActionHandler(
          newState,
          {
            payload: Object.assign(
              {},
              newState.surveyDetailsExtended,
              action.payload,
            ),
          },
        );
      } else {
        let targetQuestionnaire;

        if (checkpointId) {
          targetQuestionnaire = Object.assign({}, action.payload, {
            errors: newState.errors,
            questionnaireLogic: newState.questionnaireLogic,
          });
        } else {
          targetQuestionnaire =
            newState.revisions.questionnaireRevisions.current;
        }

        stateAfterQuestionnaireUpdate = restoreDefaultRevisionInState({
          state: newState,
          questionnaire: targetQuestionnaire,
          isQuestionnairePreview,
        });
      }

      // TODO: deprecate following when fix on backed has been applied
      stateAfterQuestionnaireUpdate = attachHasPipingOnRevisionInState(
        stateAfterQuestionnaireUpdate,
      );

      return setActiveNode(stateAfterQuestionnaireUpdate);
    }
    case DELETE_QUESTIONNAIRE_REVISION: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoading: true,
          error: null,
        },
      });
    }
    case DELETE_QUESTIONNAIRE_REVISION_SUCCESS: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoading: false,
          revisionInPreview: null,
          error: null,
        },
      });
    }
    case DELETE_QUESTIONNAIRE_REVISION_ERROR: {
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoading: false,
          error: action.error,
        },
      });
    }
    case REVERT_QUESTIONNAIRE_REVISION_LATEST_AI:
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoading: true,
        },
      });
    case REVERT_QUESTIONNAIRE_REVISION_LATEST_AI_SUCCESS:
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoading: false,
        },
      });
    case REVERT_QUESTIONNAIRE_REVISION_LATEST_AI_ERROR:
      return Object.assign({}, state, {
        revisions: {
          ...state.revisions,
          isLoading: false,
          error: action.error,
        },
      });
    case UPDATE_QUESTIONNAIRE_EXTERNAL: {
      const {
        surveyDetailsExtended: existingSurveyDetailsExtended,
        survey: existingSurveyInformation,
      } = state;
      const {
        payload: { questionnaire, saveReason, surveySettings },
      } = action;

      const questionnaireEditVersion = get(
        state,
        'surveyDetailsExtended.questionnaireEditVersion',
      );

      const updatedAction = {
        ...action,
        payload: {
          ...existingSurveyDetailsExtended,
          surveyInformation: existingSurveyInformation,
          ...transformUpsertToFetch(questionnaire),
          questionnaireEditVersion: questionnaireEditVersion + 1,
          id: get(state, 'surveyDetailsExtended.id'),
          ql: questionnaire?.ql,
        },
      };

      let updatedState = fetchQuestionnaireSuccessActionHandler(
        state,
        updatedAction,
        false,
      );
      updatedState = saveCheckpointHandler(updatedState, true, saveReason);
      const { surveyDetailsExtended = {}, activeQuestionId } = updatedState;
      const activeNodeData = getActiveNodeOrFallback(
        surveyDetailsExtended?.questions,
        activeQuestionId,
      );

      // add the language information for subsequent upsert
      if (surveySettings) {
        if (surveySettings.language) {
          updatedState = Object.assign({}, updatedState, {
            externalUpsertAdditionalInfo: {
              ...updatedState.nextUpsertAdditionalInfo,
              language: surveySettings.language,
            },
          });
        }
      }

      updatedState.questionnaireLogic = updateState({
        ql: updateQlQuestionnaire({
          nodes: surveyDetailsExtended?.questions,
          ql: updatedState.questionnaireLogic.ql,
        }),
        questionnaireLogic: updatedState.questionnaireLogic,
        shouldUpdateVersion: false,
      });

      return Object.assign(
        {},
        setActiveNode(updatedState, {
          payload: {
            refId: activeNodeData.refId,
            groupRefId: activeNodeData.groupRefId,
          },
        }),
        {
          errors: attachAIGlobalErrors(
            updatedState.errors,
            surveyDetailsExtended?.questions,
          ),
          externalUpdateTimestamp: Date.now(),
        },
      );
    }

    case CLEAR_EXTERNAL_UPSERT_ADDITIONAL_INFO:
      return Object.assign({}, state, {
        externalUpsertAdditionalInfo: {},
      });

    case FETCH_QUESTIONNAIRE:
      return Object.assign({}, state, {
        isFetching: true,
        error: false,
      });
    case FETCH_QUESTIONNAIRE_SUCCESS: {
      return fetchQuestionnaireSuccessActionHandler(state, action);
    }
    case FETCH_QUESTIONNAIRE_ERROR:
      return Object.assign({}, state, {
        isFetching: false,
        errors: action.error,
      });
    case UPDATE_QUESTIONNAIRE: {
      return updateQuestionnaireActionHandler(state, action);
    }

    case UPDATE_QUESTIONNAIRE_LOGIC: {
      const questionnaireEditVersion = get(
        state,
        'surveyDetailsExtended.questionnaireEditVersion',
      );
      const { costAffected } = action;
      return Object.assign({}, state, {
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questionnaireEditVersion: questionnaireEditVersion + 1,
          updateCostVersion: costAffected ? questionnaireEditVersion + 1 : -1,
        },
      });
    }

    case ADD_QUESTION_TO_QUESTIONNAIRE: {
      return addQuestionToQuestionnaireActionHandler(state, action);
    }

    case DUPLICATE_MULTIPLE_QUESTIONS_TO_QUESTIONNAIRE: {
      return duplicateMultipleQuestionsToQuestionnaire(state, action);
    }

    case REMOVE_QUESTION_FROM_QUESTIONNAIRE: {
      return removeQuestionFromQuestionnaireActionHandler(state, action);
    }
    case SHOW_ALL_ERRORS: {
      return showAllErrors(state, action);
    }

    case STORE_QUESTIONNAIRE_TEXTS:
    case STORE_DISQUALIFICATION_RULES:
    case STORE_QUESTIONNAIRE:
      return Object.assign({}, state, {
        isSaving: true,
      });
    case UPDATE_SURVEY_SUCCESS:
      return Object.assign({}, state, {
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          surveyPreviewTimestamp: Date.now(),
        },
      });
    case FETCH_DEMOGRAPHICS:
      return Object.assign({}, state, {
        targeting: { isFetching: true },
      });
    case FETCH_DEMOGRAPHICS_SUCCESS: {
      const { demographics = [] } = action;

      return Object.assign({}, state, {
        targeting: {
          demographics,
          hasFetched: true,
          isFetching: false,
        },
      });
    }
    case FETCH_DEMOGRAPHICS_ERROR: {
      return Object.assign({}, state, {
        targeting: {
          error: action.error,
          isFetching: false,
        },
      });
    }
    case STORE_QUESTIONNAIRE_SUCCESS: {
      const questionnaireEditVersion = get(
        state,
        'surveyDetailsExtended.questionnaireEditVersion',
      );

      const hasSavedAllVersions = action.payload === questionnaireEditVersion;
      const savedVersion = action.payload;
      const isStaleSave = savedVersion > questionnaireEditVersion;

      const newState = saveCheckpointHandler(state, false);

      return Object.assign({}, newState, {
        isSaving: !hasSavedAllVersions,
        lastSuccessfulVersion: action.payload,
        surveyDetailsExtended: {
          ...newState.surveyDetailsExtended,
          ...(!isStaleSave && { hasDifferencesInQuestionnaire: true }),
          surveyPreviewTimestamp: Date.now(),
        },
      });
    }
    case STORE_QUESTIONNAIRE_TEXTS_SUCCESS:
    case STORE_DISQUALIFICATION_RULES_SUCCESS: {
      const questionnaireEditVersion = get(
        state,
        'surveyDetailsExtended.questionnaireEditVersion',
      );

      const hasSavedAllVersions = action.payload === questionnaireEditVersion;

      return Object.assign({}, state, {
        isSaving: !hasSavedAllVersions,
        lastSuccessfulVersion: action.payload,
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          surveyPreviewTimestamp: Date.now(),
        },
      });
    }
    case FETCH_QUESTIONNAIRE_DIFF_SUCCESS: {
      return Object.assign({}, state, {
        questionsDiff: action.payload,
      });
    }
    case SET_ACTIVE_NODE: {
      return setActiveNode(state, action);
    }
    case MOVE_QUESTION: {
      return moveQuestionActionHandler(state, action);
    }
    case SET_QUESTIONNAIRE_SHUFFLING: {
      return setQuestionnaireShuffling(state, action);
    }
    case UPDATE_GROUP_PROPERTIES: {
      return updateGroupProperties(state, action);
    }

    case SHOW_MOVE_QUESTION_MODAL:
      return Object.assign({}, state, {
        isMoveQuestionModalOpen: true,
      });
    case CLOSE_MOVE_QUESTION_MODAL:
      return Object.assign({}, state, {
        isMoveQuestionModalOpen: false,
      });
    case INSERT_RECALL: {
      const { refId } = action;
      const timestamp = new Date().getTime();
      return Object.assign({}, state, {
        insertRecallRequest: { refId, timestamp },
      });
    }
    case ADD_STATEMENT: {
      return addStatementActionHandler(state, action).state;
    }
    case DELETE_STATEMENT: {
      const { nodeRefId, statementId, logicMode } = action.payload;

      const logicKey = getLogicKeyFromMode(logicMode);
      const currentQl = state[logicKey].ql;
      try {
        const newQl = questionnaireLogicService.deleteStatement(
          currentQl,
          nodeRefId,
          statementId,
        );
        const newQuestionnaireLogic = updateState({
          ql: newQl,
          questionnaireLogic: state[logicKey],
        });

        const conditionalSurveyDetailsUpdate = {};

        if (
          ![
            LOGIC_MODES.DISQUALIFICATION,
            LOGIC_MODES.HIDDEN_VARIABLES,
          ].includes(logicMode)
        ) {
          const questions = get(state, 'surveyDetailsExtended.questions', []);
          const screeningQuestions = get(
            state,
            'surveyDetailsExtended.screeningQuestions',
            [],
          );
          const { errors: globalErrors } = state;
          const { previousNodesPerNode } = newQuestionnaireLogic;
          const { questions: updatedQuestions, questionnaireErrors } =
            getRevalidatedQuestionsAndGlobalErrors({
              questions,
              screeningQuestions,
              previousNodesPerNode: mergePreviousNodesPerNode(
                state,
                logicKey,
                previousNodesPerNode,
              ),
              globalErrors,
              filter: ({ carryForward = [], type }) =>
                Boolean(carryForward.length) || isLoopAndMergeGroup(type),
              surveyConfig: getSurveyConfigForValidate(state),
            });

          conditionalSurveyDetailsUpdate.errors = questionnaireErrors;
          conditionalSurveyDetailsUpdate.surveyDetailsExtended = {
            ...state.surveyDetailsExtended,
            questions: updatedQuestions,
          };
        }

        return Object.assign({}, state, {
          [logicKey]: newQuestionnaireLogic,
          ...conditionalSurveyDetailsUpdate,
        });
      } catch (error) {
        return Object.assign({}, state, {
          [logicKey]: {
            ...state[logicKey],
            error,
          },
        });
      }
    }
    case MOVE_STATEMENT: {
      const { nodeRefId, statementId, direction, logicMode } = action.payload;
      const logicKey = getLogicKeyFromMode(logicMode);
      const currentQl = state[logicKey].ql;
      const qStatements = questionnaireLogicService.findNodeStatementsByNodeId(
        currentQl,
        nodeRefId,
      );
      const statementIndex = findIndex(qStatements.statements, {
        id: statementId,
      });
      const newPosition = statementIndex + direction;
      const updatedStatements = arrayMove(
        qStatements.statements,
        statementIndex,
        newPosition,
      );

      try {
        const newDefaultStatements = new NodeStatements({
          nodeId: nodeRefId,
          statements: updatedStatements,
        });

        const newQl = questionnaireLogicService.swapNodeStatements(
          currentQl,
          nodeRefId,
          newDefaultStatements,
        );

        const newQuestionnaireLogic = updateState({
          ql: newQl,
          questionnaireLogic: state[logicKey],
        });

        return Object.assign({}, state, {
          [logicKey]: newQuestionnaireLogic,
        });
      } catch (error) {
        return Object.assign({}, state, {
          [logicKey]: {
            ...state[logicKey],
            error,
          },
        });
      }
    }
    case SET_SCROLL_TO_STATEMENT_ID: {
      const { statementId, nodeId, valueId, valueType } = action.payload;
      const statementIdToScroll =
        'statementId' in action.payload
          ? statementId
          : findStatementIdByOutcomeActionValue({
              ql: state.questionnaireLogic.ql,
              nodeId,
              valueId,
              valueType,
            });

      return Object.assign({}, state, {
        statementIdToScroll,
      });
    }
    case UPDATE_OUTCOME: {
      return updateStatementActionHandler(state, action);
    }
    case ADD_RULE: {
      return addRuleActionHandler(state, action);
    }

    case UPDATE_RULE_OPERATOR: {
      return updateRuleOperatorActionHandler(state, action);
    }

    case UPDATE_RULE_QUOTA: {
      return updateRuleQuotaActionHandler(state, action);
    }

    case UPDATE_RULE_CONTEXT: {
      const {
        statementId,
        ruleId,
        selectedNodeId,
        matcher,
        isNumeric,
        isText,
        answerRefId,
        logicMode,
        taxonomyProperty,
        taxonomyValue,
        contextType,
        conceptRefId,
        variableRefId,
        variableValueRefId,
      } = action.payload;
      let { nodeRefId } = action.payload;

      if (logicMode === LOGIC_MODES.DISQUALIFICATION) {
        nodeRefId = DISQUALIFY_PLACEHOLDER_QUESTION.id;
      }

      try {
        let newCondition;
        if (contextType === QuestionnaireConditionContext.Taxonomy) {
          newCondition = getTaxonomyCondition({
            matcher,
            taxonomyProperty,
            defaultValue: taxonomyValue,
          });
        } else if (contextType === QuestionnaireConditionContext.Answer) {
          newCondition = getAnswerCondition({
            questionRefId: selectedNodeId,
            matcher,
            isNumeric,
            isText,
            defaultValue: answerRefId,
          });
        } else if (contextType === QuestionnaireConditionContext.Concept) {
          newCondition = getConceptCondition({
            matcher,
            nodeRefId: selectedNodeId,
            defaultValue: conceptRefId,
          });
        } else if (contextType === QuestionnaireConditionContext.Variable) {
          newCondition = getVariableCondition({
            matcher,
            variableRefId,
            defaultValue: variableValueRefId,
          });
        } else if (contextType === QuestionnaireConditionContext.Visibility) {
          newCondition = getVisibilityCondition({
            questionRefId: selectedNodeId,
            matcher,
            defaultValue: answerRefId,
          });
        }

        const logicKey = getLogicKeyFromMode(logicMode);

        let newQl = questionnaireLogicService.updateRuleConditions(
          state[logicKey].ql,
          nodeRefId,
          statementId,
          ruleId,
          [newCondition],
        );

        if (!CONDITION_CONTEXTS_SUPPORT_QUOTA.includes(contextType)) {
          newQl = questionnaireLogicService.updateRuleQuota(
            newQl,
            nodeRefId,
            statementId,
          );
        }

        const newQuestionnaireLogic = updateState({
          ql: newQl,
          questionnaireLogic: state[logicKey],
        });

        return Object.assign({}, state, {
          [logicKey]: newQuestionnaireLogic,
        });
      } catch (error) {
        return Object.assign({}, state, {
          questionnaireLogic: {
            ...state.questionnaireLogic,
            error,
          },
        });
      }
    }
    case ADD_CONDITION: {
      return addConditionActionHandler(state, action);
    }
    case DELETE_CONDITION: {
      const { statementId, ruleId, conditionId, logicMode } = action.payload;
      const targetLogic = getLogicKeyFromMode(logicMode);
      let { nodeRefId } = action.payload;
      if (logicMode === LOGIC_MODES.DISQUALIFICATION) {
        nodeRefId = DISQUALIFY_PLACEHOLDER_QUESTION.id;
      }

      try {
        const newQl = questionnaireLogicService.deleteCondition(
          state[targetLogic].ql,
          nodeRefId,
          statementId,
          ruleId,
          conditionId,
          true,
          true,
        );
        const newQuestionnaireLogic = updateState({
          ql: newQl,
          questionnaireLogic: state[targetLogic],
        });

        const conditionalSurveyDetailsUpdate = {};

        if (
          ![
            LOGIC_MODES.DISQUALIFICATION,
            LOGIC_MODES.HIDDEN_VARIABLES,
          ].includes(logicMode)
        ) {
          const questions = get(state, 'surveyDetailsExtended.questions', []);
          const screeningQuestions = get(
            state,
            'surveyDetailsExtended.screeningQuestions',
            [],
          );
          const { errors: globalErrors } = state;
          const { previousNodesPerNode } = newQuestionnaireLogic;
          const { questions: updatedQuestions, questionnaireErrors } =
            getRevalidatedQuestionsAndGlobalErrors({
              questions,
              screeningQuestions,
              previousNodesPerNode: mergePreviousNodesPerNode(
                state,
                targetLogic,
                previousNodesPerNode,
              ),
              globalErrors,
              filter: ({ carryForward = [], type }) =>
                Boolean(carryForward.length) || isLoopAndMergeGroup(type),
              surveyConfig: getSurveyConfigForValidate(state),
            });

          conditionalSurveyDetailsUpdate.errors = questionnaireErrors;
          conditionalSurveyDetailsUpdate.surveyDetailsExtended = {
            ...state.surveyDetailsExtended,
            questions: updatedQuestions,
          };
        }

        return Object.assign({}, state, {
          [targetLogic]: newQuestionnaireLogic,
          ...conditionalSurveyDetailsUpdate,
        });
      } catch (error) {
        return Object.assign({}, state, {
          [targetLogic]: {
            ...state[targetLogic],
            error,
          },
        });
      }
    }
    case UPDATE_CONDITION: {
      const { statementId, ruleId, condition, logicMode } = action.payload;
      const logicKey = getLogicKeyFromMode(logicMode);
      let { nodeRefId } = action.payload;

      if (logicMode === LOGIC_MODES.DISQUALIFICATION) {
        nodeRefId = DISQUALIFY_PLACEHOLDER_QUESTION.id;
      }

      try {
        // this is mutating the state.ql object that is why we deep copy here.
        // It should be fixed before deploying, i did it to help progress
        const currentQl = cloneDeep(state[logicKey].ql);
        const newQl = questionnaireLogicService.updateCondition(
          currentQl,
          nodeRefId,
          statementId,
          ruleId,
          condition,
        );

        const newQuestionnaireLogic = updateState({
          ql: newQl,
          questionnaireLogic: state[logicKey],
        });

        return Object.assign({}, state, {
          [logicKey]: newQuestionnaireLogic,
        });
      } catch (error) {
        return Object.assign({}, state, {
          [logicKey]: {
            ...state[logicKey],
            error,
          },
        });
      }
    }
    case LOCATION_CHANGE: {
      if (
        didLocationChangeFromTo({
          from: action.payload.previousPathname,
          to: action.payload.location.pathname,
          routes: locationResetExceptionRoutes,
        })
      ) {
        if (
          didLocationChangeFromTo({
            from: action.payload.previousPathname,
            to: action.payload.location.pathname,
            routes: getSurveyLocationResetExceptionRulesForVersionHistory(),
          })
        ) {
          return state;
        }

        const { isInRevisionPreviewMode, revisionInPreview } = state.revisions;
        let newState = { ...state };

        if (isInRevisionPreviewMode && revisionInPreview) {
          newState = restoreDefaultRevisionInState({
            state: newState,
            questionnaire: state.revisions.questionnaireRevisions.current,
            isQuestionnairePreview: false,
          });

          newState = setActiveNode(newState);
        }

        newState = Object.assign({}, newState, {
          revisions: initialState.revisions,
        });

        return newState;
      }

      return Object.assign({}, initialState);
    }

    case MOVE_NODE: {
      const { surveyDetailsExtended } = state;
      const { questions, screeningQuestions, questionnaireEditVersion } =
        surveyDetailsExtended;

      const {
        source,
        target,
        offset,
        nodesToClearMentions = [],
        saveCheckpoint = false,
      } = action.payload;

      if (
        shouldEnforceScreeningBoundary({
          screeningLogicEnabled: state.screeningLogicEnabled,
          questions,
        }) &&
        wouldCrossScreeningBoundaryOnMove({
          questions,
          source,
          target,
          offset,
        })
      ) {
        return state;
      }

      const shouldUpdateDisqualificationLogic =
        state.disqualificationLogic.version > -1;

      const nodes = clearMentionsFromNodes({
        nodes: questions,
        nodesToClearMentions,
      });

      // disqualification section start
      let nodesDisqQl;
      let newDisqualificationLogic;
      let errorsDisqQl;
      const disqUpdates = {};
      if (shouldUpdateDisqualificationLogic) {
        nodesDisqQl = cloneDeep(nodes);
        newDisqualificationLogic = state.disqualificationLogic;
        errorsDisqQl = state.errorsDisqQl || [];

        ({
          newQuestionnaireLogic: newDisqualificationLogic,
          globalErrors: errorsDisqQl,
        } = moveNodeToOtherList({
          state: {
            ...state,
            questionnaireLogic: newDisqualificationLogic,
            errors: errorsDisqQl,
          },
          nodes: nodesDisqQl,
          screeningQuestions,
          source,
          target,
          offset,
          shouldUpdateQuestionnaireLogic: false,
          isDisqualificationLogic: true,
        }));

        disqUpdates.disqualificationLogic = newDisqualificationLogic;
        disqUpdates.errorsDisqQl = errorsDisqQl;
      }
      // disqualification section end
      const { newNodes, newQuestionnaireLogic, globalErrors } =
        moveNodeToOtherList({
          state,
          nodes,
          screeningQuestions,
          source,
          target,
          offset,
        });

      const isActive = source.refId === state.activeQuestionId;

      let updatedState = state;
      if (saveCheckpoint) {
        updatedState = saveCheckpointHandler(state, true);
      }

      updatedState = Object.assign({}, updatedState, {
        errors: globalErrors,
        activeGroupId: isActive ? target.groupRefId : state.activeGroupId,
        questionnaireLogic: newQuestionnaireLogic,
        surveyDetailsExtended: {
          ...surveyDetailsExtended,
          questions: newNodes,
          questionnaireEditVersion: questionnaireEditVersion + 1,
        },
        ...disqUpdates,
      });
      return validateQuotaAndUpdateState(updatedState);
    }

    case MOVE_NODES_TO_GROUP: {
      const { surveyDetailsExtended } = state;
      const { questions, screeningQuestions, questionnaireEditVersion } =
        surveyDetailsExtended;

      const {
        payload: {
          target,
          nodesToMove,
          nodesToClearMentions = [],
          saveCheckpoint = false,
        },
      } = action;

      if (
        shouldEnforceScreeningBoundary({
          screeningLogicEnabled: state.screeningLogicEnabled,
          questions,
        }) &&
        hasScreeningNodeInMoveBatch({ questions, nodesToMove })
      ) {
        return state;
      }

      let newNodes = clearMentionsFromNodes({
        nodes: questions,
        nodesToClearMentions,
      });

      const shouldUpdateDisqualificationLogic =
        state.disqualificationLogic.version > -1;

      let newNodesDisqQl;
      let errorsDisqQl = [];
      let newDisqualificationLogic = state.disqualificationLogic;

      if (shouldUpdateDisqualificationLogic) {
        newNodesDisqQl = cloneDeep(newNodes);

        newDisqualificationLogic = state.disqualificationLogic;
        errorsDisqQl = state.errorsDisqQL || []; // TODO RES-4563: keep this namespace or not
      }

      let newQuestionnaireLogic = state.questionnaireLogic;

      let globalErrors = state.errors;
      nodesToMove.forEach((node) => {
        ({ newNodes, newQuestionnaireLogic, globalErrors } =
          moveNodeToOtherList({
            state: {
              ...state,
              questionnaireLogic: newQuestionnaireLogic,
              errors: globalErrors,
            },
            nodes: newNodes,
            screeningQuestions,
            source: node,
            target,
          }));

        if (shouldUpdateDisqualificationLogic) {
          ({
            newQuestionnaireLogic: newDisqualificationLogic,
            globalErrors: errorsDisqQl,
          } = moveNodeToOtherList({
            state: {
              ...state,
              questionnaireLogic: newDisqualificationLogic,
              errors: globalErrors,
            },
            nodes: newNodesDisqQl,
            screeningQuestions,
            source: node,
            target,
            shouldUpdateQuestionnaireLogic: false,
            isDisqualificationLogic: true,
          }));
        }
      });

      const disqUpdates = {};
      if (shouldUpdateDisqualificationLogic) {
        disqUpdates.errorsDisqQl = errorsDisqQl;
        disqUpdates.disqualificationLogic = newDisqualificationLogic;
      }

      let updatedState = state;
      if (saveCheckpoint) {
        updatedState = saveCheckpointHandler(state, true);
      }
      updatedState = Object.assign({}, updatedState, {
        errors: globalErrors,
        questionnaireLogic: newQuestionnaireLogic,
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questions: newNodes,
          questionnaireEditVersion: questionnaireEditVersion + 1,
        },
        ...disqUpdates,
      });
      return validateQuotaAndUpdateState(updatedState);
    }

    case REMOVE_GROUP_KEEP_NODES: {
      const { surveyDetailsExtended } = state;
      const { questions, screeningQuestions, questionnaireEditVersion } =
        surveyDetailsExtended;
      const {
        payload: { refId, nodesToMove, offset },
      } = action;

      let newNodes = questions;
      let globalErrors = state.errors;
      let newQuestionnaireLogic = state.questionnaireLogic;

      nodesToMove.forEach((node, index) => {
        ({ newNodes, newQuestionnaireLogic, globalErrors } = addNodeToList({
          questionnaireLogic: newQuestionnaireLogic,
          nodes: newNodes,
          node: {
            ...removeAllVariableMentionsFromNode(node),
            groupRefId: undefined,
            selectedAttributes: undefined,
          },
          screeningQuestions,
          refId: index === 0 ? refId : nodesToMove[index - 1].refId,
          errors: globalErrors,
          offset: index === 0 ? offset : 1,
        }));
      });

      return Object.assign({}, state, {
        errors: globalErrors,
        questionnaireLogic: newQuestionnaireLogic,
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questions: newNodes,
          questionnaireEditVersion: questionnaireEditVersion + 1,
        },
      });
    }

    case TOGGLE_MODAL: {
      const { name, ...rest } = action.payload;
      const modalState = state.modals[name].visible
        ? { visible: false }
        : {
            visible: true,
            ...rest,
          };

      return Object.assign({}, state, {
        modals: {
          ...state.modals,
          [name]: modalState,
        },
      });
    }
    case LOOP_AND_MERGE_UPDATE_GROUP_SUCCESS: {
      return groupLoopAndMergeUpdateGroupActionHandler(state, action);
    }

    case AB_ADD_CONCEPT: {
      return abAddConceptActionHandler(state, action);
    }
    case AB_UPDATE_CONCEPT: {
      return abUpdateConceptActionHandler(state, action);
    }
    case AB_REMOVE_CONCEPT: {
      return abRemoveConceptActionHandler(state, action);
    }

    case GROUP_ADD_CONCEPT: {
      return groupAddConceptActionHandler(state, action);
    }

    case GROUP_ADD_ATTRIBUTE: {
      return groupAddAttributeActionHandler(state, action);
    }

    case GROUP_UPDATE_ATTRIBUTE: {
      return groupUpdateAttributeActionHandler(state, action);
    }

    case GROUP_REMOVE_ATTRIBUTE: {
      return groupRemoveAttributeActionHandler(state, action);
    }

    case GROUP_REMOVE_LOOP_AND_MERGE_CONCEPT: {
      return groupRemoveConceptLoopAndMergeActionHandler(state, action);
    }

    case GROUP_RE_ORDER_LOOP_AND_MERGE_CONCEPT: {
      return groupReOrderLoopAndMergeConcept(state, action);
    }

    case GROUP_UPDATE_ATTRIBUTE_VALUE: {
      return groupUpdateAttributeValueActionHandler(state, action);
    }

    case AB_ADD_DEFAULT_NODES: {
      return abAddDefaultNodesActionHandler(state, action);
    }
    case VAN_WESTENDORP_UPDATE_SETTINGS: {
      return vanWestendorpUpdateSettingsHandler(state, action);
    }
    case VAN_WESTENDORP_UPDATE_LIKELIHOOD: {
      return vanWestendorpToggleLikelihoodNodesActionHandler(state, action);
    }
    case MATRIX_OPEN_ENDED_UPDATE_SETTINGS: {
      return matrixOpenEndedUpdateSettingsHandler(state, action);
    }
    case AB_UPDATE_PROPERTIES: {
      return abUpdatePropertiesActionHandler(state, action);
    }
    case LOOP_AND_MERGE_UPDATE_PROPERTIES: {
      return loopAndMergeUpdatePropertiesActionHandler(state, action);
    }

    case ADD_SCREENING_QUESTION: {
      return addScreeningQuestionToQuestionnaireActionHandler(state, action);
    }

    case DUPLICATE_MULTIPLE_SCREENING_QUESTIONS: {
      return addMultipleScreeningQuestionsToQuestionnaire(state, action);
    }

    case UPDATE_QUESTION_FAVORITE_STATE: {
      return updateQuestionFavoriteHandler(state, action);
    }

    case REMOVE_SCREENING_QUESTION: {
      return removeScreeningQuestionFromQuestionnaireActionHandler(
        state,
        action,
      );
    }

    case REMOVE_ALL_SCREENING_QUESTIONS:
      return removeAllScreeningQuestionFromQuestionnaireActionHandler(
        state,
        action,
      );

    case UPDATE_SCREENING_QUESTION: {
      return updateScreeningQuestionInQuestionnaireActionHandler(state, action);
    }

    case UPDATE_TARGETING: {
      const { key, taxonomyValueToBeRemoved } = action;
      if (!taxonomyValueToBeRemoved) {
        return state;
      }

      const { surveyDetailsExtended = {} } = state;
      const { questionnaireEditVersion } = surveyDetailsExtended;
      const {
        hasTaxonomyQuestionnaireLogic,
        hasTaxonomyDisqualificationLogic,
      } = hasDemographicTaxonomyWithLogic(key, {
        taxonomyWithQuestionnaireLogic:
          state.questionnaireLogic.taxonomyWithLogic || {},
        taxonomyWithDisqualificationLogic:
          state.disqualificationLogic.taxonomyWithLogic || {},
        taxonomyValueToBeRemoved,
      });

      const shouldUpdateVersion =
        hasTaxonomyQuestionnaireLogic || hasTaxonomyDisqualificationLogic;

      const updates = {};
      if (hasTaxonomyQuestionnaireLogic) {
        const newQl = questionnaireLogicService.removeConditionsWithValue(
          state.questionnaireLogic.ql,
          taxonomyValueToBeRemoved,
          QuestionnaireConditionContext.Taxonomy,
        );

        updates.questionnaireLogic = updateState({
          ql: newQl,
          questionnaireLogic: state.questionnaireLogic,
        });
      }

      const disqualificationUpdates = {};
      if (hasTaxonomyDisqualificationLogic) {
        const newQl = questionnaireLogicService.removeConditionsWithValue(
          state.disqualificationLogic.ql,
          taxonomyValueToBeRemoved,
          QuestionnaireConditionContext.Taxonomy,
        );

        disqualificationUpdates.disqualificationLogic = updateState({
          ql: newQl,
          questionnaireLogic: state.disqualificationLogic,
        });
      }

      return Object.assign({}, state, {
        ...updates,
        ...disqualificationUpdates,
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questionnaireEditVersion: shouldUpdateVersion
            ? questionnaireEditVersion + 1
            : questionnaireEditVersion,
        },
      });
    }

    case REMOVE_TARGETING_CRITERIA:
    case REMOVE_NESTING_TARGETING_CRITERIA:
    case ADD_NESTING_TARGETING_CRITERIA:
    case UPDATE_PEEQ_TARGETING:
    case UPDATE_PEEQ_COUNTRY_MULTI_SUCCESS:
    case REMOVE_AUDIENCE_SUCCESS: {
      let newState = cleanLogicFromTaxonomyProperties(
        state,
        action.payload.taxonomyProperties,
      );
      newState = cleanLogicFromTaxonomyProperties(
        state,
        action.payload.taxonomyProperties,
      );
      newState = cleanLoopAndMergeGroupsFromProfilingQuestions(
        newState,
        action.payload.loopAndMergeQuestionGroups,
      );
      return incrementQuestionnaireEditVersion(newState);
    }

    case UPDATE_PEEQ_COUNTRY_SUCCESS: {
      let newState = cleanLogicFromTaxonomyProperties(
        state,
        Object.keys(state.questionnaireLogic.taxonomyWithLogic),
      );
      newState = cleanLoopAndMergeGroupsFromProfilingQuestions(
        newState,
        state.surveyDetailsExtended.statusName === surveyStatusEnum.UNDER_EDIT
          ? []
          : action.payload.loopAndMergeQuestionGroups,
      );
      return incrementQuestionnaireEditVersion(newState);
    }

    case CONJOINT_UPDATE_PROPERTIES: {
      const { surveyDetailsExtended = {} } = state;
      const { questionnaireEditVersion } = surveyDetailsExtended;
      const { refId, properties } = action.payload;
      const { questions: nodes } = surveyDetailsExtended;

      const { newNodes, globalErrors, newQuestionnaireLogic } =
        updateConjointOrMaxDiffProperties({
          state,
          refId,
          properties,
          nodes,
        });

      return Object.assign({}, state, {
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questions: newNodes,
          questionnaireEditVersion: questionnaireEditVersion + 1,
        },
        errors: globalErrors,
        questionnaireLogic: newQuestionnaireLogic,
      });
    }

    case DOE_UPDATE_GROUP_AND_PROPERTIES: {
      const { surveyDetailsExtended = {} } = state;
      const { questionnaireEditVersion } = surveyDetailsExtended;
      const { refId, properties, blocks, choiceSets, concepts, answers } =
        action.payload;
      const { questions: nodes } = surveyDetailsExtended;
      const {
        newNodes: updatedNodes,
        globalErrors,
        newQuestionnaireLogic,
      } = updateConjointOrMaxDiffProperties({
        state,
        refId,
        properties,
        nodes,
      });
      const newNodes = updatedNodes.map((node) => {
        if (node.refId === refId) {
          // TODO: maybe handle this better, i did this in a hurry
          const question = cloneDeep(node.questions[0]);
          let newAnswers = answers;
          const existingAnswers = get(question, 'answers', []);
          const noneOfTheAboveAnswer = existingAnswers.find(
            (answer) => answer.type === ANSWER_NONE_OF_THE_ABOVE,
          );

          if (noneOfTheAboveAnswer) {
            newAnswers = [...newAnswers, noneOfTheAboveAnswer];
          }

          question.answers = newAnswers;

          return {
            ...node,
            blocks,
            choiceSets,
            concepts,
            questions: [question],
          };
        }

        return node;
      });

      return Object.assign({}, state, {
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questions: newNodes,
          questionnaireEditVersion: questionnaireEditVersion + 1,
        },
        errors: globalErrors,
        questionnaireLogic: newQuestionnaireLogic,
      });
    }

    case CONJOINT_MOVE_ATTRIBUTE: {
      const { surveyDetailsExtended = {} } = state;
      const { questionnaireEditVersion } = surveyDetailsExtended;
      const { refId, attributeRefId, direction } = action.payload;
      const { questions: nodes } = surveyDetailsExtended;

      return Object.assign({}, state, {
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questions: nodes.map((node) => {
            if (node.refId === refId) {
              const { newNodes: newAttributes } = moveNodeOnList(
                node.attributes,
                attributeRefId,
                direction,
              );

              return {
                ...node,
                attributes: newAttributes,
              };
            }

            return node;
          }),
          questionnaireEditVersion: questionnaireEditVersion + 1,
        },
      });
    }

    case FETCH_DESIGN_OF_EXPERIMENT: {
      return Object.assign({}, state, {
        isFetchingDoeSettingsData: true,
        doeErrors: [],
      });
    }
    case FETCH_DESIGN_OF_EXPERIMENT_SUCCESS: {
      return Object.assign({}, state, {
        isFetchingDoeSettingsData: false,
        doeErrors: [],
      });
    }
    case FETCH_DESIGN_OF_EXPERIMENT_ERROR: {
      const { payload: responseError = [] } = action;
      const doeErrors = get(responseError, 'body.errors', []);

      return Object.assign({}, state, {
        isFetchingDoeSettingsData: false,
        doeErrors,
      });
    }
    case TOGGLE_QUESTION_COLLAPSE: {
      const { questionRefId, groupRefId } = action;
      const { questions } = state.surveyDetailsExtended;

      return Object.assign({}, state, {
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questions: groupRefId
            ? questions.map((node) => {
                if (node.refId === groupRefId) {
                  return {
                    ...node,
                    questions: node.questions.map((question) => {
                      if (question.refId === questionRefId) {
                        return {
                          ...question,
                          isCollapsed: !question.isCollapsed,
                        };
                      }

                      return question;
                    }),
                  };
                }
                return node;
              })
            : questions.map((node) => {
                if (node.refId === questionRefId) {
                  return { ...node, isCollapsed: !node.isCollapsed };
                }
                return node;
              }),
        },
      });
    }

    case FETCH_LINK_SETTINGS_SUCCESS: {
      const survey = {
        ...state.survey,
        maxResponsesDistributionLink: action.data.maxResponses,
        // Distribution-link completes come from maxResponses (link settings), not the
        // survey-API completes. Recompute from combined state so quota validation uses the
        // correct remaining completes regardless of whether this or the questionnaire
        // fetch resolved last.
        ...deriveSurveyCompletes({
          isDistributionLink: true,
          maxResponses: action.data.maxResponses,
          surveyCompletes: state.survey.completes,
          completed: state.survey.completed,
        }),
      };
      const { newNodes, globalErrors } = getRevalidatedNodesAndGlobalErrors({
        nodes: state.surveyDetailsExtended.questions || [],
        errors: state.errors,
        previousNodesPerNode: get(
          state,
          'questionnaireLogic.previousNodesPerNode',
          {},
        ),
        surveyConfig: getSurveyConfigForValidate({
          survey,
          surveyDetailsExtended: {
            isDistributionLink: true,
          },
        }),
      });

      const updatedState = Object.assign({}, state, {
        survey,
        errors: globalErrors,
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questions: newNodes,
        },
      });
      return validateQuotaAndUpdateState(updatedState);
    }

    case SAVE_LINK_SETTINGS_SUCCESS: {
      return Object.assign({}, state, {
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          hasDifferencesInLinkSettings: true,
        },
      });
    }

    case UPDATE_LINK_SETTINGS: {
      const { name, value } = action;

      if (name === 'maxResponses') {
        const updatedState = Object.assign({}, state, {
          survey: {
            ...state.survey,
            ...deriveSurveyCompletes({
              isDistributionLink: true,
              maxResponses: value,
              completed: state.survey.completed,
            }),
            maxResponsesDistributionLink: value || undefined,
          },
        });
        return validateQuotaAndUpdateState(updatedState);
      }

      return state;
    }

    case UPDATE_SURVEY_COMPLETES: {
      const { completes } = action;

      return Object.assign({}, state, {
        survey: {
          ...state.survey,
          completes,
          remainingCompletes: completes - state.survey.completed,
        },
      });
    }

    case UPDATE_PEEQ_PROJECT_COMPLETES: {
      const { completes } = action.payload;
      const remainingCompletes = completes - state.survey.completed;

      const { newNodes, globalErrors } = getRevalidatedNodesAndGlobalErrors({
        nodes: state.surveyDetailsExtended.questions,
        errors: state.errors,
        previousNodesPerNode: get(
          state,
          'questionnaireLogic.previousNodesPerNode',
          {},
        ),
        answersWithLogicFromOutcomes:
          state.questionnaireLogic.answersWithLogicFromOutcomes,
        surveyConfig: {
          completes: remainingCompletes,
          isUnderEdit: state.survey.isUnderEdit,
          isDistributionLink: false,
          isPeeq: state.surveyDetailsExtended.isPeeq,
        },
      });

      const updatedState = Object.assign({}, state, {
        survey: {
          ...state.survey,
          completes,
          remainingCompletes,
        },
        errors: globalErrors,
        surveyDetailsExtended: {
          ...state.surveyDetailsExtended,
          questions: newNodes,
        },
      });
      return validateQuotaAndUpdateState(updatedState);
    }
    case ADD_HIDDEN_VARIABLE: {
      const newState = saveCheckpointHandler(state, true);
      return incrementQuestionnaireEditVersion(newState);
    }
    case UPDATE_HIDDEN_VARIABLE: {
      let newState = { ...state };
      const { removedValuesInLogic = [] } = action.payload;
      removedValuesInLogic.forEach((refId) => {
        newState = deleteHiddenVariableValueFromLogic(state, refId);
      });
      return incrementQuestionnaireEditVersion(newState);
    }
    case DELETE_HIDDEN_VARIABLE: {
      const { refId } = action.payload;
      let newState = deleteHiddenVariableFromLogic(state, refId);
      newState = saveCheckpointHandler(newState, true);
      return incrementQuestionnaireEditVersion(newState);
    }
    case ADD_HIDDEN_VARIABLE_VALUE:
    case UPDATE_HIDDEN_VARIABLE_VALUE: {
      return incrementQuestionnaireEditVersion(state);
    }
    case DELETE_HIDDEN_VARIABLE_VALUE: {
      const { valueRefId } = action.payload;
      const newState = deleteHiddenVariableValueFromLogic(state, valueRefId);
      return incrementQuestionnaireEditVersion(newState);
    }
    case ADD_UNCONDITIONAL_HIDE_ANSWER: {
      const newState = addUnconditionalHideAnswerActionHandler(state, action);
      return incrementQuestionnaireEditVersion(newState);
    }
    case REMOVE_UNCONDITIONAL_HIDE_ANSWER: {
      const newState = removeUnconditionalHideAnswerActionHandler(
        state,
        action,
      );
      return incrementQuestionnaireEditVersion(newState);
    }

    case TOGGLE_NODE_VISIBILITY: {
      const newState = toggleVisibilityActionHandler(state, action);

      return incrementQuestionnaireEditVersion(newState);
    }
    case VALIDATE_ALL_NODES: {
      return validateAllNodesActionHandler(state, action);
    }
    case SET_NODE_QUOTA_MODE: {
      return setNodeQuotaModeHandler(state, action);
    }
    case TOGGLE_NODE_QUOTAS_SUCCESS: {
      return toggleNodeQuotaSuccessHandler(state, action);
    }
    case TOGGLE_NODE_QUOTAS_ERROR: {
      return toggleNodeQuotaErrorHandler(state, action);
    }
    case CREATE_NODE_QUOTA_RULES: {
      return createNodeQuotaRulesActionHandler(state, action);
    }
    case REMOVE_NODE_QUOTA_RULES: {
      return removeNodeQuotaRulesActionHandler(state, action);
    }
    case UPDATE_NODE_QUOTA_RULE_VALUE: {
      return updateNodeQuotaRuleValueActionHandler(state, action);
    }
    case SET_ANSWER_SCREENER_TYPE: {
      return setAnswerScreenerTypeHandler(state, action);
    }
    case RESET_SCREENER_TYPES: {
      return resetScreenerTypesHandler(state, action);
    }
    default:
      return state;
  }
}

/**
 * @param {Boolean} shouldUpdateQuestionnaireLogic allows to skip updating (inserting) the node ids
 in the questionnaire property of the ql. This is used for disqualification rules
 where we do not update that property (the questionnaire).
 * @returns {Object}
 */
function moveNodeToOtherList({
  state,
  nodes,
  screeningQuestions,
  source,
  target,
  offset = 0,
  shouldUpdateQuestionnaireLogic = true,
  isDisqualificationLogic = false,
}) {
  const nodeToMove = {
    ...getNodeByRefId(nodes, source.refId, source.groupRefId),
    groupRefId: target.groupRefId,
    selectedAttributes: undefined,
  };

  let { newNode, globalErrors } = clearCarryForwardOnNode({
    node: nodeToMove,
    source,
    target,
    errors: state.errors,
    screeningQuestions,
    nodes,
  });

  globalErrors = globalErrors.map((error) => {
    if (error.questionRefId === newNode.refId) {
      return {
        ...error,
        groupRefId: target.groupRefId,
      };
    }

    return error;
  });

  let newNodes = getNodesOnRemove(nodes, source.refId, source.groupRefId);

  ({ newNodes, newNode } = getNodesOnAdd(
    newNodes,
    target.refId,
    target.groupRefId,
    newNode,
    offset,
  ));

  let newQuestionnaireLogic = state.questionnaireLogic;
  let newQl = newQuestionnaireLogic.ql;

  const toRoot = !target.groupRefId;
  const changedContainer =
    (source.groupRefId || target.groupRefId) &&
    source.groupRefId !== target.groupRefId;

  if (shouldUpdateQuestionnaireLogic) {
    let newIndex;
    if (toRoot) {
      newIndex = newNodes.findIndex((node) => node.refId === source.refId);
    } else {
      const groupNodes = newNodes.find(
        (group) => group.refId === target.groupRefId,
      ).questions;
      newIndex = groupNodes.findIndex((node) => node.refId === source.refId);
      if (newIndex === -1) {
        newIndex = groupNodes.length;
      }
    }

    // moving from inside a group outside
    newQl = questionnaireLogicService.moveNode(
      newQl,
      source.refId,
      newIndex,
      undefined,
      undefined,
      target.groupRefId,
    );

    const { simple: canKeepSimpleQuotas, advanced: canKeepAdvancedQuotas } =
      isGroupAllowedToHaveQuestionWithQuotas(newNodes, target.groupRefId);

    if (canKeepSimpleQuotas) {
      const originalQl = state.questionnaireLogic.ql;
      // extract simple quota from original ql for each nodes that moved

      const simpleQuotaStatements =
        questionnaireLogicService.getSimpleQuotaStatementsByNodeId(
          originalQl,
          source.refId,
        );
      forEach(simpleQuotaStatements, (statements, nodeId) => {
        newQl = questionnaireLogicService.attachQuotaStatementsByNodeId(
          newQl,
          nodeId,
          statements,
        );
      });
    }
    if (canKeepAdvancedQuotas) {
      const originalQl = state.questionnaireLogic.ql;
      // extract simple quota from original ql for each nodes that moved

      const advancedQuotaStatements =
        questionnaireLogicService.getAdvancedQuotaStatementsByNodeId(
          originalQl,
          source.refId,
        );
      forEach(advancedQuotaStatements, (statements, nodeId) => {
        newQl = questionnaireLogicService.attachQuotaStatementsByNodeId(
          newQl,
          nodeId,
          statements,
        );
      });
    }
  }
  if (changedContainer) {
    newQl = removeCarriedForwardAnswersFromQlByNodeId(
      newQl,
      nodes,
      source.refId,
    );
  }

  if (isDisqualificationLogic && target.groupRefId) {
    newQl = questionnaireLogicService.removeNodeWithLogic(newQl, source.refId);
  }

  newQuestionnaireLogic = updateState({
    ql: newQl,
    questionnaireLogic: newQuestionnaireLogic,
    shouldUpdateVersion: true,
  });

  const {
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  } = newQuestionnaireLogic;

  ({ newNodes, globalErrors } = getRevalidatedNodesAndGlobalErrors({
    nodes: newNodes,
    errors: globalErrors,
    previousNodesPerNode: newQuestionnaireLogic.previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
    screeningQuestions,
    surveyConfig: getSurveyConfigForValidate(state),
  }));

  return { newNodes, newQuestionnaireLogic, globalErrors };
}

// FETCH_QUESTIONNAIRE_SUCCESS
export function fetchQuestionnaireSuccessActionHandler(
  state,
  action,
  resetQL = false,
) {
  const {
    id: surveyId,
    ql,
    // temporary
    screeningQl,
    // end temporary
    disqualificationRules,
    isPeeq,
    isDistributionLink,
    surveyConfig,
    isQuestionnairePreview = false,
    quotas = [],
  } = action.payload;

  // merge QLs and overwrite the ql in the state with the merged one.
  // This is temporary until we have a single QL for both questionnaire and screening questions
  let mergedQl = ql;
  if (ql && screeningQl) {
    mergedQl = qlUnifier.qlUnify(ql, screeningQl, true);
  }
  //

  const disqualifiedUsers = get(disqualificationRules, 'disqualifiedUsers', 0);
  const hasManagedTargeting =
    get(surveyConfig, 'managedTargeting', false) === true;
  const screeningLogicEnabled = isPeeq && !hasManagedTargeting;

  const disqualificationLogic = disqualificationRules
    ? Object.assign({}, initialState.disqualificationLogic, {
        version: get(
          disqualificationRules,
          'disqualificationRulesEditVersion',
          -1,
        ),
        ql: disqualificationRules.json,
      })
    : initialState.disqualificationLogic;

  const currentVersion = get(
    state,
    'surveyDetailsExtended.questionnaireEditVersion',
    -1,
  );
  const receivedVersion = get(action, 'payload.questionnaireEditVersion', -1);
  // coming here means that we should not accept what the server gave us, but re-submit what we already have
  // forceAcceptServerVersion bypasses this after discard changes (reloadSurveyState with afterClearRevision)
  if (!action.forceAcceptServerVersion && currentVersion > receivedVersion) {
    Raven.captureMessage(
      '[Questionnaire]: Received version previous than already stored',
    );
    return Object.assign({}, state, {
      isFetching: false,
    });
  }

  // mapping before init logic, the groups refIds in mappedQuestions
  // need to pass in questions(nodes) array
  const { nodes, screeningQuestions } = mapQuestionsAndGroupsAsNodes(
    action.payload,
  );
  const questionGroupShuffle = get(
    action,
    'payload.questionGroupShuffle',
    SHUFFLE_NONE,
  );

  let newQuestionnaireLogic;
  if (resetQL) {
    newQuestionnaireLogic = initQlAfterFetch({
      nodes,
      questionnaireLogic: state.questionnaireLogic,
      surveyId,
    });
  } else {
    newQuestionnaireLogic = initQlAfterFetch({
      ql: mergedQl,
      questionnaireLogic: state.questionnaireLogic,
      nodes,
      surveyId,
    });
  }

  const newDisqualificationLogic = initQlAfterFetch({
    ql: disqualificationLogic.ql,
    questionnaireLogic: disqualificationLogic,
    nodes: [DISQUALIFY_PLACEHOLDER_QUESTION],
    surveyId,
    logicMode: LOGIC_MODES.DISQUALIFICATION,
  });

  const {
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  } = newQuestionnaireLogic;

  const { surveyInformation, ...surveyDetails } = action.payload;
  const { newNodes, globalErrors } = attachErrorsOnNodes({
    nodes: screeningLogicEnabled ? [...screeningQuestions, ...nodes] : nodes,
    errors: [],
    previousNodesPerNode: newQuestionnaireLogic.previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
    screeningQuestions: screeningLogicEnabled ? [] : screeningQuestions,
    surveyConfig: getSurveyConfigForValidate({
      ...state,
      survey: surveyInformation,
      surveyDetailsExtended: {
        isDistributionLink,
        isPeeq,
      },
    }),
  });

  const externalUpdateTimestamp = action.forceAcceptServerVersion
    ? Date.now()
    : state.externalUpdateTimestamp;

  const updatedState = Object.assign({}, state, {
    isFetching: false,
    hasFetched: true,
    questionGroupShuffle,
    disqualifiedUsers,
    errors: attachAIGlobalErrors(globalErrors, newNodes),
    questionnaireLogic: newQuestionnaireLogic,
    disqualificationLogic: newDisqualificationLogic,
    screeningLogicEnabled,
    lastSuccessfulVersion: receivedVersion,
    externalUpdateTimestamp,
    ...(action.forceAcceptServerVersion
      ? {
          activeQuestionId: null,
          activeGroupId: null,
          isActiveNewNode: false,
        }
      : {}),
    surveyDetailsExtended: action.payload
      ? {
          ...state.surveyDetailsExtended,
          ...surveyDetails,
          ...(action.forceAcceptServerVersion
            ? {
                hasDifferencesInQuestionnaire:
                  action.payload.hasDifferencesInQuestionnaire ?? false,
                hasDifferencesInLinkSettings:
                  action.payload.hasDifferencesInLinkSettings ?? false,
              }
            : {}),
          questions: newNodes,
          screeningQuestions: screeningLogicEnabled ? [] : screeningQuestions,
          isQuestionnairePreview,
        }
      : {},
    survey: {
      ...state.survey,
      ...surveyInformation,
      // For distribution-link surveys, override the survey-API completes with the
      // maxResponses-derived value (maxResponses preserved via ...state.survey if the link
      // settings fetch already landed; otherwise the fallback applies until it does).
      // Non-dlink surveys keep the ...surveyInformation values untouched.
      ...(isDistributionLink
        ? deriveSurveyCompletes({
            isDistributionLink: true,
            maxResponses: state.survey.maxResponsesDistributionLink,
            surveyCompletes: surveyInformation?.completes,
            completed: surveyInformation?.completed,
          })
        : {}),
    },
    questionnaireQuotaCounters: {
      pendingQuotaMode: {},
      data:
        quotas?.filter(
          (quota) => quota.quotaType === QuotaConditionTypeEnum.Simple,
        ) || [],
    },
  });
  return validateQuotaAndUpdateState(updatedState);
}

// UPDATE_QUESTIONNAIRE
// A survey-body question never becomes a screening question.
function keepSurveyBodyQuestion(previousNodes, question) {
  const previousNode = findQuestionByRefId(previousNodes, question.refId);
  if (previousNode && !previousNode.isScreening && question.isScreening) {
    return { ...question, isScreening: false };
  }
  return question;
}

export function updateQuestionnaireActionHandler(state, action) {
  // TODO: activeQuestionId is not included in action
  const { question, activeQuestionId, saveCheckpoint = false } = action;
  const previousNodes = get(state, 'surveyDetailsExtended.questions', []);
  const updatedNode = keepSurveyBodyQuestion(previousNodes, question);
  const screeningQuestions = get(
    state,
    'surveyDetailsExtended.screeningQuestions',
    [],
  );
  const questionnaireEditVersion = get(
    state,
    'surveyDetailsExtended.questionnaireEditVersion',
  );
  const logicMode = LOGIC_MODES.PLAIN;
  const targetLogic = getLogicKeyFromMode(logicMode);

  const { updatedNodes, previousNode } = getNodesOnUpdate(
    previousNodes,
    updatedNode,
    activeQuestionId,
  );

  const stateToUpdateWithoutErrors = cloneDeep(state);
  stateToUpdateWithoutErrors.surveyDetailsExtended.questions = updatedNodes;
  stateToUpdateWithoutErrors.surveyDetailsExtended.questionnaireEditVersion =
    questionnaireEditVersion + 1;

  let newQuestionnaireLogic = updateQlAfterQuestionnaireUpdate({
    state: stateToUpdateWithoutErrors,
    updatedNode,
    previousNodes,
    previousNode,
    logicMode,
    updatedNodes,
  });
  const {
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  } = newQuestionnaireLogic;

  let newDisqualificationLogic = state.disqualificationLogic;
  const shouldUpdateDisqualificationLogic =
    state.disqualificationLogic.version > -1;

  if (shouldUpdateDisqualificationLogic) {
    newDisqualificationLogic = updateQlAfterQuestionnaireUpdate({
      state: stateToUpdateWithoutErrors,
      updatedNode,
      previousNodes,
      previousNode,
      logicMode: LOGIC_MODES.DISQUALIFICATION,
      updatedNodes,
    });
  }

  const { newNodes, globalErrors } = attachErrorsOnNodes({
    nodes: updatedNodes,
    previousNodes,
    previousNodesPerNode: mergePreviousNodesPerNode(
      state,
      targetLogic,
      newQuestionnaireLogic.previousNodesPerNode,
    ),
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
    screeningQuestions,
    errors: state.errors,
    surveyConfig: getSurveyConfigForValidate(state),
  });

  let stateToUpdateWithErrors = {
    ...stateToUpdateWithoutErrors,
    errors: globalErrors,
    surveyDetailsExtended: {
      ...stateToUpdateWithoutErrors.surveyDetailsExtended,
      questions: newNodes,
    },
  };

  if (saveCheckpoint) {
    stateToUpdateWithErrors = saveCheckpointHandler(
      stateToUpdateWithErrors,
      true,
    );
  }

  const hasQuotaAssignmentStrategyChanged =
    updatedNode?.quotaAssignmentStrategy !==
    previousNode?.quotaAssignmentStrategy;

  if (hasQuotaAssignmentStrategyChanged) {
    const hasSingleAssignmentStrategy = isSingleAssignmentStrategy(
      updatedNode?.quotaAssignmentStrategy,
    );
    const hadSingleAssignmentStrategy = isSingleAssignmentStrategy(
      previousNode?.quotaAssignmentStrategy,
    );

    const changedQuotaAssignmentStrategyBetweenSingleAndMultiple =
      hasSingleAssignmentStrategy !== hadSingleAssignmentStrategy;

    if (changedQuotaAssignmentStrategyBetweenSingleAndMultiple) {
      const updatedQl = questionnaireLogicService.regenerateQuotaRuleIds(
        newQuestionnaireLogic.ql,
        updatedNode.refId,
        QuotaRuleFilter.SimpleQuota,
      );
      newQuestionnaireLogic = updateState({
        ql: updatedQl,
        questionnaireLogic: newQuestionnaireLogic,
      });
      stateToUpdateWithErrors = resetNodeQuotaAssignedHandler(
        stateToUpdateWithErrors,
        updatedNode.refId,
      );
    }
  }

  return validateQuotaAndUpdateState(
    Object.assign({}, stateToUpdateWithErrors, {
      [targetLogic]: newQuestionnaireLogic,
      disqualificationLogic: newDisqualificationLogic,
    }),
    updatedNode.refId,
  );
}
// ADD_QUESTION_TO_QUESTIONNAIRE
export function addQuestionToQuestionnaireActionHandler(state, action) {
  const {
    question: node,
    direction = ADD_QUESTION_DIRECTION.INSERT_BELOW,
    screenerTypes,
  } = action;
  let { refId } = action;
  const nodes = get(state, 'surveyDetailsExtended.questions', []);
  const { isScreening = false } = node;
  const targetLogic = getLogicKeyFromMode(LOGIC_MODES.PLAIN);
  const questionnaireEditVersion = get(
    state,
    'surveyDetailsExtended.questionnaireEditVersion',
  );

  // handle add screening question above a non screening question
  refId = addQuestionRefIdCorrection({
    refId,
    isScreening,
    screeningQuestions: nodes.filter((n) => n.isScreening),
  });

  const newNode = addNodeMetadata(node, questionnaireEditVersion + 1);

  // eslint-disable-next-line
  let { newNodes, newQuestionnaireLogic, globalErrors } = addNodeToList({
    nodes,
    node: newNode,
    screeningQuestions: nodes.filter((n) => n.isScreening),
    refId,
    errors: state.errors,
    questionnaireLogic: state[targetLogic],
    scroll: true,
    surveyConfig: getSurveyConfigForValidate(state),
    offset: direction,
    screenerTypes,
  });
  const {
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  } = newQuestionnaireLogic;

  ({ newNodes, globalErrors } = getRevalidatedNodesAndGlobalErrors({
    nodes: newNodes,
    errors: state.errors,
    previousNodesPerNode: mergePreviousNodesPerNode(
      state,
      targetLogic,
      newQuestionnaireLogic.previousNodesPerNode,
    ),
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
    screeningQuestions: nodes.filter((n) => n.isScreening),
    surveyConfig: getSurveyConfigForValidate(state),
  }));

  const newState = saveCheckpointHandler(state, true);
  const updatedState = Object.assign({}, newState, {
    shouldAddEmptyQuestion: false,
    errors: globalErrors,
    [targetLogic]: newQuestionnaireLogic,
    surveyDetailsExtended: {
      ...newState.surveyDetailsExtended,
      questions: newNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
  return validateQuotaAndUpdateState(updatedState, newNode.refId);
}

// MOVE_QUESTION
export function moveQuestionActionHandler(state, action) {
  const { surveyDetailsExtended } = state;
  const {
    questions: nodes,
    screeningQuestions,
    questionnaireEditVersion,
  } = surveyDetailsExtended;
  const {
    refId,
    groupRefId,
    direction,
    nodesToClearMentions = [],
  } = action.payload;

  const { newNodes, newPosition, nodeIndex } = getNodesOnMove(
    nodes,
    refId,
    groupRefId,
    direction,
  );

  if (
    !groupRefId &&
    shouldEnforceScreeningBoundary({
      screeningLogicEnabled: state.screeningLogicEnabled,
      questions: nodes,
    }) &&
    wouldCrossScreeningBoundary({
      questions: nodes,
      refId,
      newIndex: newPosition,
    })
  ) {
    return state;
  }

  const targetLogic = LOGIC_MODE_KEYS.PLAIN;
  let updatedNodes = newNodes;
  let newQuestionnaireLogic;
  let isMovedNodeScreening = false;

  if (groupRefId) {
    // const groupNode = getNodeByRefId(updatedNodes, groupRefId);

    // newQuestionnaireLogic = updateState({
    //   ql: questionnaireLogicService.updateGroupNode(
    //     state[targetLogic].ql,
    //     groupRefId,
    //     { nodes: getQlSubNodes(groupNode) },
    //   ),
    //   questionnaireLogic: state[targetLogic],
    // });
    const newQl = questionnaireLogicService.moveNode(
      state[targetLogic].ql,
      refId,
      newPosition,
      undefined,
      undefined,
      groupRefId,
    );
    newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: state[targetLogic],
    });
  } else {
    isMovedNodeScreening = nodes[nodeIndex].isScreening;

    newQuestionnaireLogic = updateQlAfterMoveQuestion({
      questionnaireLogic: state[targetLogic],
      questionId: refId,
      direction,
    });
  }

  const {
    previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  } = newQuestionnaireLogic;

  let { errors: globalErrors } = state;
  // there is no CF on sub nodes or screening questions
  if (!groupRefId && !isMovedNodeScreening) {
    updatedNodes = updatedNodes.map((node) => {
      // Screening questions don't depend on the survey body's order, and part
      // of their errors come from the quota pass: keep them.
      if (node.isScreening) return node;
      const nodeErrors = getNodeErrors({
        node,
        nodes: updatedNodes,
        screeningQuestions,
        previousNodesPerNode,
        answersWithLogicFromOutcomes,
        answersWithLogicFromOutcomesHide,
        nodesWithLogicFromOutcomesShow,
        hiddenNodes,
      });

      globalErrors = updateQuestionnaireErrors({
        globalErrors,
        nodeErrors,
        nodeRefId: node.refId,
        nodeType: node.type,
      });

      return { ...node, errors: nodeErrors };
    });
  }

  updatedNodes = clearMentionsFromNodes({
    scrollToRefId: refId,
    nodes: updatedNodes,
    nodesToClearMentions,
  });

  return Object.assign({}, state, {
    errors: globalErrors,
    [targetLogic]: newQuestionnaireLogic,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: updatedNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
}
// UPDATE_GROUP_PROPERTIES
function updateGroupProperties(state, action) {
  const { payload } = action;
  const { property, refId } = payload;

  const conditionalUpdates = {};
  const groupTypeChanged = property === 'type';
  const shuffleEnabled =
    property === 'shuffle' && payload.value !== SHUFFLE_NONE;
  const singePageViewEnabled =
    property === 'displaySamePage' && payload.value === true;
  const mustRemoveLogicFromSubnodes = singePageViewEnabled || shuffleEnabled;

  let newState = groupTypeChanged ? saveCheckpointHandler(state, true) : state;

  const { surveyDetailsExtended = {}, questionnaireLogic } = newState;
  const { questionnaireEditVersion, questions: nodes } = surveyDetailsExtended;

  const updatedQuestions = updateGroupPropertiesHandler(
    {
      ...payload,
    },
    nodes,
    get(state, 'questionnaireLogic.previousNodesPerNode', {}),
  );

  let newQl = cloneDeep(questionnaireLogic.ql);
  let newQuestionnaireLogic = questionnaireLogic;
  if (mustRemoveLogicFromSubnodes) {
    // remove logic from group questions
    const groupNode = updatedQuestions.find((q) => q.refId === refId);

    if (!groupNode) {
      throw new Error(`Node with refId ${refId} not found`);
    }

    newQl = questionnaireLogicService.resetSubnodeStatementsByGroupId(
      newQl,
      refId,
    );
    // extract simple quota from original ql for all nodes inside the group
    // and re-attach them to the new ql, since they should not be removed when shuffling
    const simpleQuotaStatements =
      questionnaireLogicService.getSimpleQuotaStatementsByNodeId(
        questionnaireLogic.ql,
      );
    forEach(simpleQuotaStatements, (statements, nodeId) => {
      newQl = questionnaireLogicService.attachQuotaStatementsByNodeId(
        newQl,
        nodeId,
        statements,
      );
    });
    newQuestionnaireLogic = updateState({
      ql: newQl,
      questionnaireLogic: newQuestionnaireLogic,
    });
  }

  if (groupTypeChanged) {
    const node = updatedQuestions.find((q) => q.refId === refId);

    if (!node) {
      throw new Error(`Node with refId ${refId} not found`);
    }

    // update the questionnaire logic
    const previousNode = nodes.find((q) => q.refId === refId);
    const basicToLoopAndMerge =
      isBasicGroup(previousNode?.type) && isLoopAndMergeGroup(node?.type);
    const loopAndMergeToBasic =
      isBasicGroup(node?.type) && isLoopAndMergeGroup(previousNode?.type);

    if (loopAndMergeToBasic) {
      // remove L&M adl rules
      const { concepts = [] } = previousNode;
      concepts.forEach((concept) => {
        newQl = questionnaireLogicService.removeConditionsWithValue(
          newQl,
          concept.refId,
          QuestionnaireConditionContext.Concept,
        );
      });
    } else if (basicToLoopAndMerge) {
      const subNodeRefIds = previousNode.questions.map(
        ({ refId: subNodeRefId }) => subNodeRefId,
      );
      newQl = removeGroupStatementsReferencingSubnodes(
        newQl,
        refId,
        subNodeRefIds,
      );
    }

    newQuestionnaireLogic = updateState({
      ql: questionnaireLogicService.updateGroupNode(newQl, refId, {
        nodes: getQlSubNodes(node),
        repetitions: node.properties?.repetitions,
        type: node.type,
      }),
      questionnaireLogic: newQuestionnaireLogic,
    });

    // update global errors
    const { errors: nodeErrors } = node;
    const newGlobalErrors = updateQuestionnaireErrors({
      globalErrors: newState.errors,
      nodeRefId: node.refId,
      groupRefId: node.groupRefId,
      nodeErrors,
      nodeType: node.type,
    });

    conditionalUpdates.errors = newGlobalErrors;

    if (!GROUP_TYPES_ALLOW_SIMPLE_QUOTAS.includes(node.type)) {
      const subNodeRefIds = node.questions.map((q) => q.refId);
      subNodeRefIds.forEach((subNodeRefId) => {
        newState = removeNodeQuotaRulesActionHandler(newState, {
          questionRefId: subNodeRefId,
          matcher: QuotaConditionTypeEnum.Simple,
        });
      });
    }
    if (!GROUP_TYPES_ALLOW_ADVANCED_QUOTAS.includes(node.type)) {
      const subNodeRefIds = node.questions.map((q) => q.refId);
      subNodeRefIds.forEach((subNodeRefId) => {
        newState = removeNodeQuotaRulesActionHandler(newState, {
          questionRefId: subNodeRefId,
          matcher: QuotaConditionTypeEnum.Advanced,
        });
      });
    }
  }

  const updatedState = Object.assign({}, newState, {
    ...conditionalUpdates,
    questionnaireLogic: newQuestionnaireLogic,
    surveyDetailsExtended: {
      ...newState.surveyDetailsExtended,
      questions: updatedQuestions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
  return validateQuotaAndUpdateState(updatedState);
}
// SHOW_ALL_ERRORS
function showAllErrors(state) {
  const previousNodes = get(state, 'surveyDetailsExtended.questions', []);
  const screeningQuestions = get(
    state,
    'surveyDetailsExtended.screeningQuestions',
    [],
  );
  const previousNodesPerNode = get(
    state,
    'questionnaireLogic.previousNodesPerNode',
    {},
  );

  const { newNodes } = attachErrorsOnNodes({
    nodes: previousNodes,
    clearPristineValues: true,
    previousNodesPerNode,
    answersWithLogicFromOutcomes:
      state.questionnaireLogic.answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide:
      state.questionnaireLogic.answersWithLogicFromOutcomesHide,
    screeningQuestions,
    surveyConfig: getSurveyConfigForValidate(state),
  });

  const updatedState = Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newNodes,
      pristineTimestamp: Date.now(),
    },
  });

  return validateQuotaAndUpdateState(updatedState);
}

function setNodeQuotaModeHandler(state, action) {
  const { questionRefId, mode } = action.payload;
  return Object.assign({}, state, {
    questionnaireQuotaCounters: {
      ...state.questionnaireQuotaCounters,
      pendingQuotaMode: {
        ...state.questionnaireQuotaCounters.pendingQuotaMode,
        [questionRefId]: mode,
      },
    },
  });
}

function clearPendingQuotaMode(state, questionRefId) {
  const { [questionRefId]: removed, ...rest } =
    state.questionnaireQuotaCounters.pendingQuotaMode || {};
  return Object.assign({}, state, {
    questionnaireQuotaCounters: {
      ...state.questionnaireQuotaCounters,
      pendingQuotaMode: rest,
    },
  });
}

function toggleNodeQuotaErrorHandler(state, action) {
  return clearPendingQuotaMode(state, action.questionRefId);
}

function resetNodeQuotaAssignedHandler(state, questionRefId) {
  return Object.assign({}, state, {
    questionnaireQuotaCounters: {
      ...state.questionnaireQuotaCounters,
      data: state.questionnaireQuotaCounters.data.map((quota) => {
        if (quota.questionRefId === questionRefId) {
          return {
            ...quota,
            previousRevisionsAssigned: quota.resultsCount || 0,
          };
        }
        return quota;
      }),
    },
  });
}

function toggleNodeQuotaSuccessHandler(state, action) {
  const { questionRefId, questionQuotas = [] } = action.payload;
  const questionnaireEditVersion = get(
    state,
    'surveyDetailsExtended.questionnaireEditVersion',
  );

  const releasedState = clearPendingQuotaMode(state, questionRefId);

  const nextState = Object.assign({}, releasedState, {
    questionnaireQuotaCounters: {
      ...releasedState.questionnaireQuotaCounters,
      data: [
        ...releasedState.questionnaireQuotaCounters.data.filter(
          (quota) => quota.questionRefId !== questionRefId,
        ),
        ...questionQuotas,
      ],
    },
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });

  return validateQuotaAndUpdateState(nextState, questionRefId);
}

export function removeNodeQuotaRulesActionHandler(state, action) {
  const { questionRefId, matcher = QuotaConditionTypeEnum.Simple } = action;
  const {
    surveyDetailsExtended,
    questionnaireLogic: { ql },
  } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;

  const qStatements = questionnaireLogicService.findNodeStatementsByNodeId(
    ql,
    questionRefId,
  );
  const nonMatchingQuotaStatements = qStatements.statements.filter(
    ({ rule }) => rule?.quota?.context?.type !== matcher,
  );

  const newStatements = new NodeStatements({
    nodeId: questionRefId,
    statements: nonMatchingQuotaStatements,
  });

  const newQl = questionnaireLogicService.swapNodeStatements(
    ql,
    questionRefId,
    newStatements,
  );

  const updatedStateLogic = updateState({
    ql: newQl,
    questionnaireLogic: state.questionnaireLogic,
    shouldUpdateVersion: true,
  });

  let updatedState = Object.assign({}, state, {
    questionnaireLogic: updatedStateLogic,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });

  updatedState = saveCheckpointHandler(updatedState, true);

  return validateQuotaAndUpdateState(updatedState, questionRefId);
}

function createNodeQuotaRulesActionHandler(state, action) {
  const { questionRefId, totalCompletes, mode = QUOTA_MODE.MAX } = action;
  const { surveyDetailsExtended } = state;
  const { questions: nodes, questionnaireEditVersion } = surveyDetailsExtended;
  const workingNode = findQuestionByRefId(nodes, questionRefId);

  if (!workingNode) {
    // eslint-disable-next-line no-console
    console.error(
      '[createNodeQuotaRulesActionHandler] - workingNode not found',
    );
    return state;
  }

  const isMinimum = mode === QUOTA_MODE.MIN;
  const unconditionallyHiddenItems = getUnconditionallyHiddenItems(state);

  const quotaEligibleOptions = getQuotaEligibleOptions({
    node: workingNode,
    unconditionallyHiddenItems,
    isEligibleItem: getQuotaEligibilityForNode({
      node: workingNode,
      ql: state.questionnaireLogic.ql,
      unconditionallyHiddenItems,
    }),
  });

  // calculate quota per answer
  const newQl = addQuotaRulesForSpecificAnswersColumns({
    questionRefId,
    questionType: workingNode.type,
    ...quotaEligibleOptions,
    totalQuota: isMinimum ? 0 : totalCompletes,
    ql: state.questionnaireLogic.ql,
    isScreening: Boolean(workingNode.isScreening),
  });

  const questions = isMinimum
    ? stampMinimumResponsesMarker(nodes, questionRefId)
    : nodes;

  const updatedState = Object.assign({}, state, {
    questionnaireLogic: updateState({
      ql: newQl,
      questionnaireLogic: state.questionnaireLogic,
      shouldUpdateVersion: true,
    }),
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
  return validateQuotaAndUpdateState(updatedState, questionRefId);
}

function stampMinimumResponsesMarker(nodes, questionRefId) {
  return nodes.map((node) => {
    if (node.refId === questionRefId) {
      return { ...node, quotaLimitType: QUOTA_LIMIT_TYPE_MIN };
    }
    if (node.questions) {
      return {
        ...node,
        questions: stampMinimumResponsesMarker(node.questions, questionRefId),
      };
    }
    return node;
  });
}

function updateNodeQuotaRuleValueActionHandler(state, action) {
  const { questionRefId, ruleId, answerRefId, columnRefId, value } = action;
  const {
    questionnaireLogic: { ql },
    surveyDetailsExtended: { questionnaireEditVersion },
  } = state;

  const hasValidValue = isFinite(value);

  // Resolve the target rule by answer identity, preferring it over the passed
  // ruleId. When a quota is temporarily cleared the rule can be stripped by
  // cleanFromEmptyValues, leaving a stale/undefined ruleId; matching on the
  // answer keeps subsequent edits working (update-or-create).
  const simpleQuotaStatements =
    questionnaireLogicService.getSimpleQuotaStatementsByNodeId(
      ql,
      questionRefId,
    )[questionRefId] || [];

  const matchesAnswer = (rule) => {
    const condition = rule?.conditions?.[0];
    if (!condition) return false;
    if (columnRefId) {
      return (
        condition.value?.row === answerRefId &&
        condition.value?.column === columnRefId
      );
    }
    return condition.value === answerRefId;
  };

  const matchingStatement =
    simpleQuotaStatements.find(({ rule }) => matchesAnswer(rule)) ||
    (ruleId && simpleQuotaStatements.find(({ rule }) => rule?.id === ruleId));

  let newQl = cloneDeep(ql);

  if (matchingStatement) {
    // Rule exists: update its value. An empty value clears it, so
    // cleanFromEmptyValues removes it on save and validation shows "missing".
    newQl = questionnaireLogicService.updateRuleQuotaValue(
      newQl,
      questionRefId,
      matchingStatement.rule.id,
      () => (hasValidValue ? value : undefined),
    );
  } else if (hasValidValue && answerRefId) {
    // No rule for this answer yet (e.g. previously cleared and removed): create,
    // unless the item can't carry one (a screening answer that isn't allowed).
    const node = findQuestionByRefId(
      state.surveyDetailsExtended.questions,
      questionRefId,
    );
    const isEligibleItem = getQuotaEligibilityForNode({
      node,
      ql,
      unconditionallyHiddenItems: getUnconditionallyHiddenItems(state),
    });
    if (isEligibleItem && !isEligibleItem({ answerRefId, columnRefId })) {
      return state;
    }
    newQl = columnRefId
      ? generateQuotaRuleForAnswerColumn({
          questionRefId,
          answerRefId,
          columnRefId,
          quotaValue: value,
          ql: newQl,
        })
      : generateQuotaRuleForAnswer({
          questionRefId,
          answerRefId,
          quotaValue: value,
          ql: newQl,
          isScreening: Boolean(node?.isScreening),
        });
  } else {
    // Nothing to update and no valid value to set: leave the logic untouched.
    return state;
  }

  const updatedState = Object.assign({}, state, {
    questionnaireLogic: updateState({
      ql: newQl,
      questionnaireLogic: state.questionnaireLogic,
      shouldUpdateVersion: true,
    }),
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });

  return validateQuotaAndUpdateState(updatedState, questionRefId);
}

export default questionnairePageNewReducer;
